import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import theme from 'theme';
import * as DOMPurify from 'dompurify';

export type Props = {
    htmlContent: string;
    selectedLink?: string;
    useIframe?: boolean;
    displayConditions?: Record<string, string>;
};

export const highlightLink = (selectedLink: string, htmlContent: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    const selectedLinkCSS = `
      <style>
        .parent {
          position: relative;
        }
        .backdrop {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
        opacity: 0.82;
        }
        .highlight {
          display: inline-block;
          position: relative;
        }
        .highlight::before {
          content: "";
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          left: -30px;
          width: 0;
          height: 0;
          border-top: 10px solid transparent;
          border-bottom: 10px solid transparent;
          border-left: 20px solid ${theme.palette.action.active};
        }
      </style>
    `;

    const head = doc.head;
    const style = doc.createElement('style');
    style.innerHTML = selectedLinkCSS;
    head.appendChild(style);

    const body = doc.body;
    const parent = doc.createElement('div');
    parent.classList.add('parent');
    body.prepend(parent);

    const backdrop = doc.createElement('div');
    backdrop.classList.add('backdrop');
    parent.appendChild(backdrop);

    const links = doc.querySelectorAll(`a[href="${selectedLink}"], a[href='${selectedLink}']`);
    links.forEach((link) => {
        const highlight = doc.createElement('span');
        highlight.classList.add('highlight');
        link.parentNode.insertBefore(highlight, link);
        highlight.appendChild(link);
    });

    return doc.documentElement.outerHTML;
};

/**
 * Adds interactive hover tooltips to HTML elements with display conditions
 *
 * This function:
 * 1. Finds all template blocks in the format {% if condition %} ... {% endif %}
 * 2. Wraps the content inside these blocks with hover-enabled elements
 * 3. Adds mouse event listeners for hover detection
 * 4. Uses React state to control tooltip visibility and positioning
 * 5. Highlights the section with a colored outline during hover
 *
 * Expected HTML structure:
 * ```html
 * {% if conditionName %}
 *   <!-- Email content with display condition -->
 * {% endif %}
 * ```
 *
 * @param containerRef - React ref to the container holding the HTML content
 * @param displayConditions - Map of condition names to their display labels
 * @param setTooltip - Function to update tooltip state
 * @returns Cleanup function to remove event listeners
 */
export const addDisplayConditionHovers = (
    containerRef: React.RefObject<HTMLDivElement>,
    displayConditions: Record<string, string>,
    setTooltip: (tooltip: { visible: boolean; content: string; x: number; y: number } | null) => void
) => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Process the HTML to wrap conditional blocks with hover elements
    const processedHtml = processConditionalBlocksForHover(container.innerHTML, displayConditions);
    if (processedHtml !== container.innerHTML) {
        container.innerHTML = processedHtml;
    }

    // Find all elements with display condition attributes (that we just added)
    const elementsWithConditions = container.querySelectorAll('[data-display-condition]');

    elementsWithConditions.forEach((element) => {
        const conditionName = element.getAttribute('data-display-condition');
        const conditionLabel = conditionName ? displayConditions[conditionName] : null;

        if (!conditionLabel) return;

        const htmlElement = element as HTMLElement;
        htmlElement.style.transition = 'transform 0.2s ease';
        htmlElement.style.transformOrigin = 'center center';

        const handleMouseEnter = () => {
            htmlElement.style.transform = 'scale(1.03)';

            const containerRect = container.getBoundingClientRect();
            const elementRect = htmlElement.getBoundingClientRect();

            const relativeLeft = elementRect.left - containerRect.left;
            const relativeTop = elementRect.top - containerRect.top;

            const offsetLeft = htmlElement.offsetLeft;
            const offsetTop = htmlElement.offsetTop;

            const finalX = relativeLeft < 0 ? offsetLeft : relativeLeft;
            const finalY = relativeTop < 0 ? offsetTop : relativeTop;

            setTooltip({
                visible: true,
                content: conditionLabel,
                x: finalX,
                y: finalY + 24,
            });
        };

        const handleMouseLeave = () => {
            htmlElement.style.transform = '';

            setTooltip(null);
        };

        htmlElement.addEventListener('mouseenter', handleMouseEnter);
        htmlElement.addEventListener('mouseleave', handleMouseLeave);

        (htmlElement as any)._cleanupDisplayCondition = () => {
            htmlElement.removeEventListener('mouseenter', handleMouseEnter);
            htmlElement.removeEventListener('mouseleave', handleMouseLeave);
        };
    });

    return () => {
        const elementsToCleanup = container.querySelectorAll('[data-display-condition]');
        elementsToCleanup.forEach((element) => {
            const cleanup = (element as any)._cleanupDisplayCondition;
            if (cleanup) cleanup();
        });
    };
};

const processConditionalBlocksForHover = (htmlContent: string, displayConditions: Record<string, string>): string => {
    // Regex to match {% if condition %} ... {% endif %} blocks
    const conditionalRegex = /{%\s*if\s+(\w+)\s*%}([\s\S]*?){%\s*endif\s*%}/g;

    return htmlContent.replace(conditionalRegex, (match, conditionKey, content) => {
        // Only wrap if we have a display condition for this key
        if (displayConditions[conditionKey]) {
            // Ensure proper dimensions for hover detection, no default border
            return `<div data-display-condition="${conditionKey}" style="display: block; position: relative;">${content}</div>`;
        }
        return content;
    });
};

const HtmlRenderer = ({ htmlContent, selectedLink, useIframe = false, displayConditions }: Props) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [tooltip, setTooltip] = useState<{ visible: boolean; content: string; x: number; y: number } | null>(null);

    const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
        ADD_TAGS: ['#comment'],
        WHOLE_DOCUMENT: true,
    });

    // Handle iframe content loading
    useEffect(() => {
        if (useIframe && iframeRef.current && htmlContent) {
            const iframe = iframeRef.current;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

            if (iframeDoc) {
                // Clear existing content
                iframeDoc.open();
                iframeDoc.close();

                // Create the HTML structure programmatically
                const doctype = iframeDoc.implementation.createDocumentType('html', '', '');
                iframeDoc.insertBefore(doctype, iframeDoc.documentElement);

                // Add meta tags
                const meta = iframeDoc.createElement('meta');
                meta.setAttribute('charset', 'utf-8');
                iframeDoc.head.appendChild(meta);

                const viewport = iframeDoc.createElement('meta');
                viewport.setAttribute('name', 'viewport');
                viewport.setAttribute('content', 'width=device-width, initial-scale=1');
                iframeDoc.head.appendChild(viewport);

                // Add styles
                const style = iframeDoc.createElement('style');
                style.textContent = `
                    body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; }
                    img { max-width: 100%; }
                `;
                iframeDoc.head.appendChild(style);

                // Add sanitized content
                iframeDoc.body.innerHTML = sanitizedHtml;

                // Add link highlighting if needed
                if (selectedLink && iframeDoc.body) {
                    const links = iframeDoc.querySelectorAll(`a[href="${selectedLink}"], a[href='${selectedLink}']`);

                    // Scroll link into view
                    if (links.length) {
                        setTimeout(() => {
                            links[0].scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                    }
                }
            }
        }
    }, [htmlContent, selectedLink, useIframe, sanitizedHtml]);

    // Handle direct rendering scroll
    useEffect(() => {
        if (!useIframe && selectedLink) {
            const link = document.querySelector(`a[href="${selectedLink}"], a[href='${selectedLink}']`);
            if (link) {
                link.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [selectedLink, useIframe]);

    // Handle display condition hover tooltips for direct rendering
    useEffect(() => {
        if (!useIframe && displayConditions && Object.keys(displayConditions).length > 0) {
            // Small delay to ensure DOM is ready after dangerouslySetInnerHTML

            const cleanup = addDisplayConditionHovers(containerRef, displayConditions, setTooltip);

            // Store cleanup function for later use
            if (containerRef.current) {
                (containerRef.current as any)._displayConditionCleanup = cleanup;
            }

            // Capture the current ref value for cleanup
            const currentContainer = containerRef.current;

            return () => {
                if (currentContainer) {
                    const cleanup = (currentContainer as any)._displayConditionCleanup;
                    if (cleanup) {
                        cleanup();
                        delete (currentContainer as any)._displayConditionCleanup;
                    }
                }
                setTooltip(null);
            };
        }
    }, [htmlContent, displayConditions, useIframe]);

    const getTooltipStyle = () => {
        if (!tooltip || !containerRef.current) {
            return { display: 'none' } as const;
        }

        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        let { x, y } = tooltip;

        const tooltipWidth = 200;
        if (x + tooltipWidth > containerWidth) {
            x = containerWidth - tooltipWidth - 10;
        }
        if (x <= 0) {
            x = 10;
        }

        const tooltipHeight = 40;
        let finalY = y;
        let transform = 'translateY(-100%)';

        if (y - tooltipHeight < 0) {
            finalY = y + 40;
            transform = 'translateY(0)';
        }

        if (finalY + tooltipHeight > containerHeight) {
            finalY = containerHeight - tooltipHeight - 10;
            transform = 'translateY(0)';
        }

        if (finalY < 0) {
            finalY = 10;
            transform = 'translateY(0)';
        }

        return {
            position: 'absolute' as const,
            left: `${x}px`,
            top: `${finalY}px`,
            transform: transform,
            background: theme.palette.action.active,
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            fontFamily: theme.typography.fontFamily,
            fontWeight: 500,
            zIndex: 10000,
            pointerEvents: 'none' as const,
            opacity: tooltip.visible ? 1 : 0,
            transition: 'opacity 0.2s ease',
            whiteSpace: 'nowrap' as const,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        };
    };

    if (useIframe) {
        return (
            <Box height="100%" overflow="hidden">
                <iframe
                    ref={iframeRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                    }}
                    title="Content Preview"
                />
            </Box>
        );
    }

    return (
        <Box position="relative" overflow="hidden">
            <div
                ref={containerRef}
                dangerouslySetInnerHTML={{
                    __html: selectedLink ? highlightLink(selectedLink, sanitizedHtml) : sanitizedHtml,
                }}
            />
            {tooltip && <div style={getTooltipStyle()}>{tooltip.content}</div>}
        </Box>
    );
};

export default HtmlRenderer;
